import React, { useEffect, useState } from "react";
import ModalComponent from "../../../components/modals";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SimpleSelect from "../../../components/selects/simpleSelect";
import FormCalendar from "../../../components/calendar/formCalendar";
import {
  callApiAsync,
  getCallAvailableInventoryFile,
  getCallInventoryFile,
  getCallInventoryFileByImportMonth,
  getCallInventoryFileByImportRange,
  getInventoryDownloadYears
} from "../../../services/apiService";
import { formatDateOnly } from "../../../services/dateService";
import { months } from "../../../constants/selectData";

const creationValidationSchema = yup.object().shape({
  creationYear: yup.string().required("El año es obligatorio")
});

const importMonthValidationSchema = yup.object().shape({
  importYear: yup.string().required("El año es obligatorio"),
  importMonth: yup.string().required("El mes es obligatorio")
});

const importRangeValidationSchema = yup.object().shape({
  startDate: yup.date().nullable().required("La fecha inicial es obligatoria"),
  endDate: yup
    .date()
    .nullable()
    .required("La fecha final es obligatoria")
    .test(
      "end-date-after-start-date",
      "La fecha final debe ser mayor o igual a la fecha inicial",
      function (value) {
        const { startDate } = this.parent;

        if (!startDate || !value) return true;

        return value >= startDate;
      }
    )
});

const cardStyle = {
  border: "1px solid #E5E7EB",
  borderRadius: "14px",
  padding: "18px",
  backgroundColor: "#FFFFFF"
};

const softCardStyle = {
  ...cardStyle,
  backgroundColor: "#F9FAFB"
};

const helperTextStyle = {
  margin: "4px 0 14px",
  fontSize: "13px",
  lineHeight: "18px",
  color: "#6B7280"
};

const dividerStyle = {
  height: "1px",
  width: "100%",
  backgroundColor: "#E5E7EB",
  margin: "20px 0"
};

const downloadBlob = (response, fileName) => {
  if (!(response.data instanceof Blob)) {
    console.error("No se recibió un Blob:", response.data);
    return;
  }

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};

const InventaryDownload = ({
  open,
  handleClose,
  userId,
  inventoryFilters = {}
}) => {
  const [creationYearOptions, setCreationYearOptions] = useState([]);
  const [importYearOptions, setImportYearOptions] = useState([]);

  const {
    control: creationControl,
    getValues: getCreationValues,
    reset: resetCreationForm,
    handleSubmit: handleSubmitCreation,
    formState: { errors: creationErrors }
  } = useForm({
    resolver: yupResolver(creationValidationSchema),
    defaultValues: {
      creationYear: ""
    }
  });

  const {
    control: importMonthControl,
    getValues: getImportMonthValues,
    reset: resetImportMonthForm,
    handleSubmit: handleSubmitImportMonth,
    formState: { errors: importMonthErrors }
  } = useForm({
    resolver: yupResolver(importMonthValidationSchema),
    defaultValues: {
      importYear: "",
      importMonth: ""
    }
  });

  const {
    control: importRangeControl,
    getValues: getImportRangeValues,
    reset: resetImportRangeForm,
    handleSubmit: handleSubmitImportRange,
    watch,
    formState: { errors: importRangeErrors }
  } = useForm({
    resolver: yupResolver(importRangeValidationSchema),
    defaultValues: {
      startDate: null,
      endDate: null
    }
  });

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const loadInventoryDownloadYears = () => {
    callApiAsync(
      () => getInventoryDownloadYears(),
      (data) => {
        setCreationYearOptions(data?.creationYears || []);
        setImportYearOptions(data?.importYears || []);
      }
    );
  };

  useEffect(() => {
    if (open) {
      loadInventoryDownloadYears();
    }
  }, [open]);

  const downloadInventoryByCreationYear = () => {
    const { creationYear } = getCreationValues();

    callApiAsync(async () => {
      const response = await getCallInventoryFile(creationYear);
      downloadBlob(response, `inventory_creation_${creationYear}.xlsx`);
    });
  };

  const downloadAvailableInventory = () => {
    callApiAsync(async () => {
      const response = await getCallAvailableInventoryFile(
        userId,
        inventoryFilters
      );

      downloadBlob(response, "inventory_available.xlsx");
    });
  };

  const downloadInventoryByImportMonth = () => {
    const { importYear, importMonth } = getImportMonthValues();

    callApiAsync(async () => {
      const response = await getCallInventoryFileByImportMonth(
        importYear,
        importMonth
      );

      downloadBlob(
        response,
        `inventory_import_${importYear}_${importMonth}.xlsx`
      );
    });
  };

  const downloadInventoryByImportRange = () => {
    const { startDate, endDate } = getImportRangeValues();

    const formattedStartDate = formatDateOnly(startDate);
    const formattedEndDate = formatDateOnly(endDate);

    callApiAsync(async () => {
      const response = await getCallInventoryFileByImportRange(
        formattedStartDate,
        formattedEndDate
      );

      downloadBlob(
        response,
        `inventory_import_${formattedStartDate}_to_${formattedEndDate}.xlsx`
      );
    });
  };

  const handleCloseModal = () => {
    resetCreationForm({
      creationYear: ""
    });

    resetImportMonthForm({
      importYear: "",
      importMonth: ""
    });

    resetImportRangeForm({
      startDate: null,
      endDate: null
    });

    handleClose();
  };

  return (
    <ModalComponent
      open={open}
      handleClose={handleCloseModal}
      title="Descargar inventario"
      maxWidth="800px"
      actions={<></>}
    >
      <div className="VContainer">
        <div style={softCardStyle}>
          <p className="createRist-title ml-0 mt-0">
            Todo el inventario disponible
          </p>

          <p style={helperTextStyle}>
            Descarga todos los equipos disponibles para este usuario con los
            filtros actualmente aplicados en la tabla principal.
          </p>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <p style={{ ...helperTextStyle, marginBottom: 0 }}>
                Este archivo no descarga toda la base de datos, solo los
                registros que estan disponibles en tu vista.
              </p>
            </Grid>

            <Grid item xs={12} md={3}>
              <div className="HContainer j-center">
                <LaddaButton
                  variant="contained"
                  onClick={downloadAvailableInventory}
                >
                  <div className="HContainer align-center">
                    <p className="button_text">Descargar todo</p>
                  </div>
                </LaddaButton>
              </div>
            </Grid>
          </Grid>
        </div>

        <div style={dividerStyle} />

        <div style={cardStyle}>
          <p className="createRist-title ml-0 mt-0">
            Inventario por fecha de creación
          </p>

          <p style={helperTextStyle}>
            Este descargable corresponde al flujo actual. Filtra los equipos
            según el Año en que fueron creados en el sistema.
          </p>

          <form
            onSubmit={handleSubmitCreation(downloadInventoryByCreationYear)}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={9}>
                <p className="detail-label">Año del inventario:</p>
                <SimpleSelect
                  name="creationYear"
                  control={creationControl}
                  hideLabel={true}
                  options={creationYearOptions}
                  margin="0"
                />
                {creationErrors.creationYear && (
                  <p className="red-message">
                    {creationErrors.creationYear.message}
                  </p>
                )}
              </Grid>

              <Grid item xs={12} md={3}>
                <div className="HContainer j-center">
                  <LaddaButton variant="contained" type="submit">
                    <div className="HContainer align-center">
                      <p className="button_text">Descargar actual</p>
                    </div>
                  </LaddaButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </div>

        <div style={dividerStyle} />

        <div style={softCardStyle}>
          <p className="createRist-title ml-0 mt-0">
            Inventario por fecha de importación
          </p>

          <p style={helperTextStyle}>
    Este descargable filtra automáticamente los equipos cuya fecha de importación está dentro del período seleccionado.
          </p>

          <form
            onSubmit={handleSubmitImportMonth(downloadInventoryByImportMonth)}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={4.5}>
                <p className="detail-label">Año:</p>
                <SimpleSelect
                  name="importYear"
                  control={importMonthControl}
                  hideLabel={true}
                  options={importYearOptions}
                  margin="0"
                />
                {importMonthErrors.importYear && (
                  <p className="red-message">
                    {importMonthErrors.importYear.message}
                  </p>
                )}
              </Grid>

              <Grid item xs={12} md={4.5}>
                <p className="detail-label">Mes:</p>
                <SimpleSelect
                  name="importMonth"
                  control={importMonthControl}
                  hideLabel={true}
                  options={months}
                  margin="0"
                />
                {importMonthErrors.importMonth && (
                  <p className="red-message">
                    {importMonthErrors.importMonth.message}
                  </p>
                )}
              </Grid>

              <Grid item xs={12} md={3}>
                <div className="HContainer j-center">
                  <LaddaButton variant="contained" type="submit">
                    <div className="HContainer align-center">
                      <p className="button_text">Descargar mes</p>
                    </div>
                  </LaddaButton>
                </div>
              </Grid>
            </Grid>
          </form>

          <div style={dividerStyle} />

          <form
            onSubmit={handleSubmitImportRange(downloadInventoryByImportRange)}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={4.5}>
                <p className="detail-label">Fecha inicial:</p>
                <FormCalendar
                  name="startDate"
                  control={importRangeControl}
                  width="100%"
                  error={importRangeErrors.startDate}
                  defaultValue={null}
                  maxDate={endDate || undefined}
                  margin="0"
                />
              </Grid>

              <Grid item xs={12} md={4.5}>
                <p className="detail-label">Fecha final:</p>
                <FormCalendar
                  name="endDate"
                  control={importRangeControl}
                  width="100%"
                  error={importRangeErrors.endDate}
                  defaultValue={null}
                  minDate={startDate || undefined}
                  margin="0"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <div className="HContainer j-center">
                  <LaddaButton variant="contained" type="submit">
                    <div className="HContainer align-center">
                      <p className="button_text">Descargar rango</p>
                    </div>
                  </LaddaButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    </ModalComponent>
  );
};

export default InventaryDownload;
